/**
 * Card view of the client list — the alternative to the table on the wider
 * list pages.
 *
 * A card opens the read-only view, not the editor: browsing a list is a
 * looking action, and the pencil is right there when a change is wanted.
 */
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store/AppContext.jsx';
import { useWindowed } from './MoreRows.jsx';
import { formatDate } from '../lib/dates.js';
import Icon from '../lib/icons.jsx';
import {
  Card, EmptyState, Avatar, TypePill, PlanPill, PayPill, ProgressBar, OwnerPill, planTone
} from './Primitives.jsx';

const trialLabel = d =>
  (d.dT < 0
    ? `${Math.abs(d.dT)} ${Math.abs(d.dT) === 1 ? 'day' : 'days'} over`
    : `${d.dT} ${d.dT === 1 ? 'day' : 'days'} left`);

export default function ClientGrid({ list, count }) {
  /* Above the empty-state return: see ClientTable. */
  const [page, more] = useWindowed(list);
  const { S, derive, setSheet, deleteClients } = useApp();
  const navigate = useNavigate();
  const openClient = id => navigate(`/clients/${id}`);

  if (!list.length) {
    return (
      <Card>
        {count ? <div className="tcount">{count}</div> : null}
        <EmptyState title="Nothing here yet">
          Adjust the filters, or add a client to get started.
        </EmptyState>
      </Card>
    );
  }

  const remove = r => {
    // eslint-disable-next-line no-alert
    if (window.confirm(`Delete "${r.name || 'this client'}"?`)) deleteClients([r.id]);
  };

  /* Buttons sit inside a clickable card, so each one stops the card opening. */
  const act = (e, fn) => { e.stopPropagation(); fn(); };

  return (
    <Card>
      {count ? <div className="tcount">{count}</div> : null}
      <div className="cards">
        {page.map(r => {
          const d = derive(r);

          return (
            <div
              className="ccard"
              /* The top edge takes the plan's own gradient, so a wall of cards
                 reads as a mix of tiers rather than one repeated stripe. */
              data-tier={planTone(r.plan)}
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => openClient(r.id)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openClient(r.id);
                }
              }}
            >
              <div className="top2">
                <Avatar name={r.name} />
                <div className="tmeta">
                  <b title={r.name || 'Untitled'}>{r.name || 'Untitled'}</b>
                  <small title={r.email || ''}>{r.email || 'no email'}</small>
                </div>
                <div className="acts">
                  <button type="button" className="vw" title="View details"
                    onClick={e => act(e, () => openClient(r.id))}>
                    <Icon name="eye" />
                  </button>
                  <button type="button" title="Edit"
                    onClick={e => act(e, () => setSheet({ kind: 'client', id: r.id }))}>
                    <Icon name="edit" />
                  </button>
                  <button type="button" className="rm" title="Delete"
                    onClick={e => act(e, () => remove(r))}>
                    <Icon name="trash" />
                  </button>
                </div>
              </div>

              <div className="pills">
                <TypePill value={d.type} />
                <PlanPill value={r.plan} />
                <PayPill value={r.pay} paidValues={S.paidValues} />
                {/*
                  * What brought them in.
                  *
                  * Both are columns on the table and neither was on the card,
                  * so half the module answered "where did this account come
                  * from" and half did not — and the card is the half most
                  * people look at. The code is shown only when there is one;
                  * Direct is the default and saying it 1,221 times says
                  * nothing.
                  */}
                {r.promo ? <span className="cpromo"><Icon name="tag" />{r.promo}</span> : null}
                {r.source && r.source !== 'Direct'
                  ? <span className="csource"><Icon name="zap" />{r.source}</span> : null}
                {/*
                  * Carried across by somebody here, rather than registered.
                  *
                  * Shown only on those: nearly every account signed itself up
                  * and marking twelve hundred of them "registered" says
                  * nothing. These are the handful a manager is counting, and
                  * they carry the owner who moved them in the Owner column
                  * beside this.
                  */}
                {r.cameFrom ? (
                  <span className={`cfrom ${r.cameFrom}`}>
                    <Icon name={r.cameFrom === 'pipeline' ? 'zap' : 'card'} />
                    {r.cameFrom === 'pipeline' ? 'From pipeline' : 'From deals'}
                  </span>
                ) : null}
              </div>

              {d.pct !== null ? <ProgressBar pct={d.pct} tone={d.pcls} /> : null}

              <div className="foot">
                <OwnerPill name={r.handler} />
                <span className="fdate">
                  {d.exp ? formatDate(d.exp, false, S.dateFmt) : d.trial ? trialLabel(d) : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      {more}
    </Card>
  );
}
